import type { RagaRecord } from '@swarasense/shared';
import { ragaSeedFor } from '../api/seed.js';
import { loadProfile, updateProfile } from '../api/profile.js';

/**
 * Ear training, Task 1: the "known ragas" MODEL — persistence + preset
 * filters + a starter default + resolve/validate helpers — for the "Which
 * raga?" exercise's upcoming "choose your known ragas" picker. This module
 * is deliberately UI-free (no DOM): a later task builds the picker on top of
 * it, and `app.ts` wires the exercise's target/distractor pool to whatever
 * `resolveKnownRagas(loadKnownRagaNames())` returns.
 *
 * The persisted state is just a list of raga NAMES (not full records) —
 * `resolveKnownRagas` re-resolves them against the live seed bundle on every
 * read, so a raga that's renamed/removed in a future seed update degrades
 * gracefully (silently dropped) rather than persisting a stale/broken
 * record.
 *
 * Phase A: this list now lives in `profile.myRagas` (`api/profile.ts`) so
 * the ear picker and the "You" profile view share ONE "My ragas" list
 * instead of two. `loadProfile`'s one-time migration moves whatever was
 * under this module's old standalone `swarasense.earKnownRagas` key into the
 * profile the first time it's loaded, so nothing already-chosen is lost.
 */

/** Fewest known ragas the "Which raga?" exercise can build a real
 * multiple-choice round from (a target + at least 2 distractors). */
export const MIN_KNOWN_RAGAS = 3;

export interface RagaPreset {
  id: string;
  label: string;
  /** Pure — real raga names present in `all`, never a name absent from it
   * (so callers never have to separately validate a preset's output). */
  ragaNames: (all: RagaRecord[]) => string[];
}

/**
 * Case-insensitive name/alias lookup scoped to whatever record list is
 * passed in — the same alias-aware matching `api/seed.ts`'s `ragaSeedFor`
 * does against the live singleton bundle, but over an arbitrary `all` so
 * preset/starter logic stays PURE (a function of its arguments only) and
 * testable without touching seed.ts's module-level state.
 */
function findByNameOrAlias(name: string, all: readonly RagaRecord[]): RagaRecord | undefined {
  const lower = name.toLowerCase();
  return all.find(
    (r) => r.name.toLowerCase() === lower || r.aliases.some((a) => a.toLowerCase() === lower),
  );
}

/** Dedup by `.name`, preserving first-seen order. */
function dedupByName(records: readonly RagaRecord[]): RagaRecord[] {
  const seen = new Set<string>();
  const out: RagaRecord[] = [];
  for (const r of records) {
    if (seen.has(r.name)) continue;
    seen.add(r.name);
    out.push(r);
  }
  return out;
}

/** "verified"/"single-source" ragas have a hand-curated gamaka/character
 * write-up (the ~45 enriched ragas) — the same rule `raga-rail.ts`'s
 * `isEnrichedRaga` and `scripts/gen-content/raga-page.ts`'s
 * `ENRICHED_STATUSES` use, kept as our own copy here (this module is the
 * data-model layer, not a UI import of the rail). */
const ENRICHED_STATUSES = new Set(['verified', 'single-source']);

/**
 * A curated handful of well-known, commonly-taught ragas — the "Popular
 * starters" preset and `starterSet`'s fill-in. Entries are common/alias
 * spellings (e.g. "Shankarabharanam", "Todi") resolved via
 * `findByNameOrAlias` against whatever bundle is passed in, so a name not
 * present in `all` (e.g. a trimmed test fixture) is silently dropped rather
 * than producing a broken/unresolved entry.
 */
const POPULAR_RAGA_NAMES = [
  'Mohanam',
  'Hamsadhwani',
  'Mayamalavagowla',
  'Shankarabharanam',
  'Kalyani',
  'Kambhoji',
  'Kharaharapriya',
  'Todi',
];

function resolvePresentByName(names: readonly string[], all: RagaRecord[]): RagaRecord[] {
  const records = names
    .map((name) => findByNameOrAlias(name, all))
    .filter((r): r is RagaRecord => r !== undefined);
  return dedupByName(records);
}

function popularStarters(all: RagaRecord[]): string[] {
  return resolvePresentByName(POPULAR_RAGA_NAMES, all).map((r) => r.name);
}

function curatedNotesRagas(all: RagaRecord[]): string[] {
  return all.filter((r) => ENRICHED_STATUSES.has(r.validation_status)).map((r) => r.name);
}

function pentatonicRagas(all: RagaRecord[]): string[] {
  return all.filter((r) => r.janya_type === 'audava-audava').map((r) => r.name);
}

export const RAGA_PRESETS: RagaPreset[] = [
  { id: 'popular-starters', label: 'Popular starters', ragaNames: popularStarters },
  { id: 'curated-notes', label: 'Ragas with curated notes', ragaNames: curatedNotesRagas },
  { id: 'pentatonic', label: 'Pentatonic', ragaNames: pentatonicRagas },
];

/**
 * The persisted known-raga names (`profile.myRagas`), `[]` if nothing's been
 * chosen yet or storage is unreadable/broken (private browsing, corrupt
 * JSON, storage disabled) — guarded (via `loadProfile`) so a broken
 * localStorage never breaks the exercise, it just falls back to "nothing
 * chosen yet".
 */
export function loadKnownRagaNames(): string[] {
  return loadProfile().myRagas ?? [];
}

/** Persists the known-raga names into `profile.myRagas`; a no-op (never
 * throws) if storage is unavailable (private browsing, storage disabled). */
export function saveKnownRagaNames(names: string[]): void {
  updateProfile({ myRagas: names });
}

/** A handful of names to pre-fill "known ragas" with before the reader has
 * chosen anything: the raga they're CURRENTLY practising (if it resolves in
 * `all`) plus a few common ragas from `POPULAR_RAGA_NAMES` — enough to be
 * immediately playable (comfortably above `MIN_KNOWN_RAGAS`). Only names
 * present in `all`; deduped. */
export function starterSet(currentRagaName: string, all: RagaRecord[]): string[] {
  const current = findByNameOrAlias(currentRagaName, all);
  const fill = resolvePresentByName(POPULAR_RAGA_NAMES, all);
  const combined = dedupByName(current ? [current, ...fill] : fill);
  return combined.map((r) => r.name);
}

/**
 * Names → real `RagaRecord`s via `ragaSeedFor` (alias-aware, against the
 * live seed bundle) — unknown/unresolvable names are dropped (e.g. a raga
 * renamed or removed since the names were persisted), and the result is
 * deduped by name (a reader could persist "Todi" and "Hanumatodi" — the
 * same raga under two names — and should only see it once).
 */
export function resolveKnownRagas(names: string[]): RagaRecord[] {
  const records = names
    .map((name) => ragaSeedFor(name))
    .filter((r): r is RagaRecord => r !== undefined);
  return dedupByName(records);
}

/** Whether `records` is enough to build a real "Which raga?" round from (a
 * target + at least `MIN_KNOWN_RAGAS - 1` distractors). */
export function isPlayableSet(records: RagaRecord[]): boolean {
  return records.length >= MIN_KNOWN_RAGAS;
}
